import { faSave, faSpinner } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Formik } from 'formik'
import React, { useEffect, useRef, useState } from 'react'
import { Alert, Button, Card, Col, Form, Row } from 'react-bootstrap'
import { toast } from 'react-toastify'
import * as Yup from 'yup'
import {
  ADULT_AGE,
  KENNITALA_LENGTH,
  ageFromKennitala,
  isAdultAge,
  normalizeSsn,
  setYupLocale,
} from '../utils/formUtils'

import { useTranslation } from 'react-i18next'
import {
  ApplicationResponse,
  checkExemption,
  createApplication,
  searchMyFamily,
  updateApplication,
} from '../api/registration'

import { useDispatch, useSelector } from 'react-redux'
import { Link, useHistory, useParams } from 'react-router-dom'
import { AppDispatch } from '../store'
import { fetchApplication } from '../store/applicationSlice'
import { fetchTownDependencies } from '../store/createApplicationSlice'
import { RootState } from '../store/rootReducer'
import CreateApplicationBasic from './CreateApplicationBasic'
import CreateApplicationContacts from './CreateApplicationContacts'
import CreateApplicationData from './CreateApplicationData'
import CreateApplicationDisclaimer from './CreateApplicationDisclaimer'

setYupLocale(Yup, 'is')

export interface CreateApplicationContactFormValues {
  ssn: string
  name: string
  email: string
  mobile: string
}

export interface CreateApplicationFormValues {
  ssn: string
  ssnValid: boolean

  birthYear: number | undefined
  ageYear: number | undefined
  ageNow: number | undefined
  isExemption: boolean | undefined

  name: string
  email: string
  mobile: string
  account: string
  usePersonalAllowance: boolean
  specialNeeds: string
  hasSpecialNeeds: boolean
  other: string
  town: string
  workplaceType: string
  workplaceDistrict: string
  workplace: string[]
  // otherWorkplace: string

  period: string
  hasParts: boolean
  parts: string[]
  numPartsApplication: number
  numWorkplacesApplication: number
  prioritizeWorkplacesApplication: boolean
  filterWorkplacesApplication: boolean

  hasDisclaimer: boolean
  disclaimerAccepted: boolean

  contacts: CreateApplicationContactFormValues[]
}

const initialValuesTemplate = {
  ssn: '',
  ssnValid: false,
  birthYear: undefined,
  ageYear: undefined,
  ageNow: undefined,
  isExemption: undefined,
  name: '',
  email: '',
  mobile: '',
  account: '',
  usePersonalAllowance: false,
  specialNeeds: '',
  hasSpecialNeeds: false,
  other: '',
  town: '',
  workplaceType: '',
  workplaceDistrict: '',
  workplace: [],
  // otherWorkplace: '',
  period: '',
  parts: [],
  numPartsApplication: 0,
  hasParts: false,
  numWorkplacesApplication: 0,
  prioritizeWorkplacesApplication: false,
  filterWorkplacesApplication: false,
  hasDisclaimer: false,
  disclaimerAccepted: false,
  contacts: [],
}

const CreateApplication = (): React.ReactElement | null => {
  const { id } = useParams<{ id: string | undefined }>()
  const history = useHistory()
  const dispatch = useDispatch<AppDispatch>()
  const topRef = useRef<HTMLDivElement>(null)
  const { t, i18n } = useTranslation(['createApplication', 'common'])
  const { currentUser } = useSelector((state: RootState) => state.auth)
  const [validationSchema, setValidationSchema] = useState<Yup.AnySchema | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [initialValues, setInitialValues] = useState<CreateApplicationFormValues>({
    ...initialValuesTemplate,
  })
  const [errorList, setErrorList] = useState<string[]>([])
  const [existingApplication, setExistingApplication] = useState<string | undefined>(undefined)
  const applicationState = useSelector((state: RootState) => state.application)
  const townsEntities = useSelector((state: RootState) => state.createApplication.towns.entities)

  useEffect(() => {
    setYupLocale(Yup, i18n.language)
    const guardianRequired = (root: CreateApplicationFormValues): boolean => {
      if (isAdultAge(root.ageNow)) return false
      const town = root.town ? townsEntities[root.town] : undefined
      return !!town?.requireGuardianForUnder18
    }
    setValidationSchema(
      Yup.object().shape({
        ssn: Yup.string()
          .label(t('kennitala'))
          .required()
          .test('checkFamilySsn', t('kennitalaFinnstEkki'), (value, context) => {
            if (id != null || value === initialValues.ssn) {
              return true
            }
            setInitialValues({
              ...initialValuesTemplate,
              ssn: value || '',
              ssnValid: false,
            })
            if (value && value.length === 10) {
              return new Promise((resolve, reject) => {
                searchMyFamily(value)
                  .then((r) => {
                    const applicantIsCurrentUser = currentUser?.ssn === value
                    const applicantIsAdult = isAdultAge(r.data.age_now)
                    const emptyContact = { name: '', ssn: '', email: '', mobile: '' }
                    setInitialValues({
                      ...initialValuesTemplate,
                      ssn: value,
                      ssnValid: true,
                      name: r.data.name,
                      ageNow: r.data.age_now,
                      contacts: applicantIsCurrentUser
                        ? applicantIsAdult
                          ? []
                          : [emptyContact]
                        : [
                            {
                              name: currentUser?.name || '',
                              ssn: currentUser?.ssn || '',
                              email: '',
                              mobile: '',
                            },
                          ],
                    })
                    resolve(true)
                  })
                  .catch((e) => {
                    if (!('isAxiosError' in e && e.response && e.response.status === 400)) {
                      toast.error(t('common:kerfisvilla'))
                      return context.createError({
                        message: t('common:kerfisvilla'),
                      })
                    }
                    resolve(false)
                  })
              })
            }
            return context.createError({
              message: t('kennitalaEkkiGild'),
            })
          }),
        name: Yup.string(),
        email: Yup.string().label(t('netfang')).email().required(),
        mobile: Yup.string().label(t('farsimi')).required(),
        account: Yup.string()
          .label(t('reikningur'))
          .required()
          .matches(/^\d{1,4}-\d{1,2}-\d{1,6}$/, t('reikningurVilla')),
        specialNeeds: Yup.string().label(t('sertharfir')),
        hasSpecialNeeds: Yup.boolean().label(t('hefurSertharfir')),
        other: Yup.string().label(t('annad')),

        town: Yup.string().label(t('sveitarfelag')).required(),
        workplaceType: Yup.string().label(t('tegundStarfsstadar')),
        workplaceDistrict: Yup.string().label(t('hverfiStarfsstadar')),
        numWorkplacesApplication: Yup.number().required(),
        prioritizeWorkplacesApplication: Yup.boolean().required(),
        filterWorkplacesApplication: Yup.boolean().required(),
        workplace: Yup.array()
          .of(Yup.string())
          .label(t('starfsstadur'))
          .required()
          .min(1, t('veljaEitt'))
          .when(
            'numWorkplacesApplication',
            (numWorkplacesApplication: number, schema: Yup.ArraySchema<Yup.AnySchema>) => {
              return numWorkplacesApplication > 0
                ? schema.max(numWorkplacesApplication, `${t('veljaHamark')} ${numWorkplacesApplication}`)
                : schema
            }
          ),
        // otherWorkplace: Yup.string().label(t('annadStarfsstadur')),
        period: Yup.string().label(t('timabil')).required(),
        numPartsApplication: Yup.number().required(),
        hasParts: Yup.boolean().required(),
        parts: Yup.array()
          .of(Yup.string())
          .label(t('starfstimabil'))
          .required()
          .when('hasParts', (hasParts: boolean, schema: Yup.ArraySchema<Yup.AnySchema>) => {
            return hasParts ? schema.min(1, t('veljaEitt')) : schema
          })
          .when('numPartsApplication', (numPartsApplication: number, schema: Yup.ArraySchema<Yup.AnySchema>) => {
            return numPartsApplication > 0
              ? schema.max(numPartsApplication, `${t('veljaHamark')} ${numPartsApplication}`)
              : schema
          }),
        disclaimerAccepted: Yup.boolean().when('hasDisclaimer', (hasDisclaimer: boolean, schema: Yup.BooleanSchema) =>
          hasDisclaimer ? schema.oneOf([true], t('disclaimer.required')) : schema
        ),
        contacts: Yup.array()
          .of(
            Yup.object().shape({
              ssn: Yup.string()
                .label(t('kennitala'))
                .required()
                .min(KENNITALA_LENGTH, t('kennitalaEkkiGild'))
                .test('notApplicant', t('guardianCannotBeApplicant'), function (ssn) {
                  // yup 0.32 exposes `from` at runtime but doesn't type it on TestContext
                  type WithFrom = { from?: { value: CreateApplicationFormValues }[] }
                  const from = (this as unknown as WithFrom).from
                  const rootSsn = from?.[from.length - 1]?.value?.ssn
                  const normalized = normalizeSsn(ssn)
                  return !normalized || !rootSsn || normalized !== normalizeSsn(rootSsn)
                })
                .test('guardianAdult', t('guardianMustBeAdult'), (ssn) => {
                  const normalized = normalizeSsn(ssn)
                  if (normalized.length < KENNITALA_LENGTH) return true
                  const age = ageFromKennitala(normalized)
                  return age == null || age >= ADULT_AGE
                }),
              name: Yup.string().label(t('nafn')).required(),
              email: Yup.string().label(t('netfang')).email().required(),
              mobile: Yup.string().label(t('farsimi')).required(),
            })
          )
          .label(t('adstandendur'))
          .required()
          .test('guardianRequiredForMinor', t('veljaEitt'), function (contacts) {
            const root = this.parent as CreateApplicationFormValues
            if (!guardianRequired(root)) return true
            return !!contacts && contacts.length >= 1
          })
          .test('guardianMustBeOther', t('guardianMustBeOther'), function (contacts) {
            const root = this.parent as CreateApplicationFormValues
            if (!guardianRequired(root)) return true
            const applicantSsn = normalizeSsn(root.ssn)
            if (!contacts || !applicantSsn) return true
            return contacts.some((c) => {
              const ssn = normalizeSsn(c?.ssn)
              if (!ssn || ssn === applicantSsn) return false
              const age = ageFromKennitala(ssn)
              return age != null && age >= ADULT_AGE
            })
          }),
      })
    )
  }, [t, i18n.language, initialValues, currentUser, id, townsEntities])

  useEffect(() => {
    if (id != null) {
      dispatch(fetchApplication(id))
        .then((r) => {
          const applicationResponse = r.payload as ApplicationResponse

          checkExemption(applicationResponse.town.id, applicationResponse.ssn)
            .then((r2) => {
              setInitialValues({
                ...initialValuesTemplate,
                ssn: applicationResponse.ssn,
                name: applicationResponse.name,
                ssnValid: true,
                birthYear: r2.data.year,
                ageYear: r2.data.age_year,
                ageNow: r2.data.age_now,
                isExemption: r2.data.is_exemption,
                email: applicationResponse.email,
                mobile: applicationResponse.tel,
                account: applicationResponse.account,
                usePersonalAllowance: applicationResponse.use_personal_allowance || false,
                specialNeeds: applicationResponse.special_needs || '',
                hasSpecialNeeds: applicationResponse.has_special_needs || false,
                other: applicationResponse.other,

                town: applicationResponse.town.id,
                period: applicationResponse.period.id,
                workplace: applicationResponse.workplaces.map((w) => w.id),
                // otherWorkplace: applicationResponse.other_workplace,
                parts: applicationResponse.period_parts?.map((p) => p.period_part.id) || [],

                contacts: applicationResponse.contacts.map((c) => ({
                  name: c.name,
                  ssn: c.ssn,
                  email: c.email,
                  mobile: c.tel,
                })),
              })

              dispatch(fetchTownDependencies(applicationResponse.town.id, applicationResponse.ssn))
            })
            .catch(() => {
              toast.error(t('common:kerfisvilla'))
            })
        })
        .catch(() => {
          toast.error(t('common:kerfisvilla'))
        })
    }
  }, [dispatch, id, t])

  if (validationSchema == null) {
    return null
  }

  if (id != null && applicationState.application == null) {
    return (
      <Card>
        <Card.Body>
          <FontAwesomeIcon spin icon={faSpinner} />
        </Card.Body>
      </Card>
    )
  }

  return (
    <div ref={topRef}>
      <Formik
        initialValues={initialValues}
        validateOnBlur={false}
        // validateOnChange={false}
        validationSchema={validationSchema}
        enableReinitialize
        onSubmit={(values, actions): void => {
          setSubmitting(true)
          setErrorList([])
          setExistingApplication(undefined)

          const data = {
            ssn: values.ssn,
            email: values.email,
            tel: values.mobile,
            account: values.account,
            use_personal_allowance: values.usePersonalAllowance,
            special_needs: values.specialNeeds,
            has_special_needs: values.hasSpecialNeeds,
            other: values.other,

            town: values.town,
            period: values.period,
            period_parts: values.parts,
            workplace: values.workplace,
            // other_workplace: values.otherWorkplace,

            disclaimer_accepted: values.disclaimerAccepted,

            contacts: values.contacts.map((c) => ({
              ssn: c.ssn,
              name: c.name,
              tel: c.mobile,
              email: c.email,
            })),
          }

          const promise = id != null ? updateApplication(id, data) : createApplication(data)

          promise
            .then((response) => {
              if (id != null) {
                toast.success(`Umsókn fyrir „${values.name}“ hefur verið breytt.`)
                history.push(`/umsokn/${id}`)
              } else {
                toast.success(`Umsókn fyrir „${values.name}“ hefur verið stofnuð.`)
                history.push(`/umsokn/${response.data.id}`)
              }
            })
            .catch((e) => {
              setSubmitting(false)
              const errorList: string[] = []
              let existingApplication: string | undefined

              if ('isAxiosError' in e && e.response && e.response.status === 400) {
                if (Array.isArray(e.response.data)) {
                  e.response.data.forEach((err: string) => errorList.push(err))
                } else {
                  const { non_field_errors: nonFieldErrors, ...other } = e.response.data

                  if (nonFieldErrors) {
                    nonFieldErrors.forEach((err: string) => {
                      errorList.push(err)
                    })
                  }

                  Object.entries(other).forEach(([k, v]) =>
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    (v as any).forEach((e: string) => actions.setFieldError(k, e))
                  )
                }

                errorList.forEach((el) => {
                  if (el.replace(/[^#]/g, '').length === 2) {
                    existingApplication = el.substring(el.indexOf('#') + 1, el.lastIndexOf('#'))
                  }
                })

                setErrorList(errorList)
                setExistingApplication(existingApplication)
                topRef?.current?.scrollIntoView()
                toast.error(t('villaIForminu'))
              } else {
                toast.error(`Óvænt villa kom upp. Vinsamlegast reyndu aftur.`)
              }
            })
        }}
      >
        {({ handleReset, handleSubmit, setStatus, values }): React.ReactElement => {
          const selectedTown = values.town ? townsEntities[values.town] : undefined
          const isGuardianBlocked =
            !!selectedTown?.requireGuardianForUnder18 &&
            values.ageNow !== undefined &&
            !isAdultAge(values.ageNow) &&
            currentUser?.ssn === values.ssn
          return (
            <Form
              noValidate
              onReset={handleReset}
              onSubmit={(e: React.FormEvent<HTMLFormElement>): void => {
                setStatus('submitted')
                handleSubmit(e)
              }}
            >
              {errorList.length > 0 && (
                <Alert variant="danger" className="mt-3">
                  {errorList
                    .map((x) => (existingApplication ? x.replace(`#${existingApplication}#`, '') : x))
                    .map((e) => (
                      <div key={e}>{e}</div>
                    ))}
                  {existingApplication && (
                    <div className="mt-2">
                      <Link style={{ color: '#000', fontStyle: 'italic' }} to={`/umsokn/${existingApplication}`}>
                        {t('smelltuHerSkoda')}
                      </Link>
                    </div>
                  )}
                </Alert>
              )}
              <CreateApplicationBasic />
              <CreateApplicationData />
              {!isGuardianBlocked && <CreateApplicationContacts />}
              <CreateApplicationDisclaimer />
              {!isGuardianBlocked && values.ssnValid && values.town && (
                <Card className="mb-2">
                  <Card.Body>
                    <Row>
                      <Col>
                        <h4>{t('sendaInn')}</h4>
                      </Col>
                    </Row>
                    <Row>
                      <Col md="4">
                        <p className="small text-muted">{t('sendaInnHjalp')}</p>
                      </Col>
                      <Col md="8">
                        <Button type="submit" variant="primary" className="w-100" disabled={submitting}>
                          <FontAwesomeIcon icon={faSave} /> {t('sendaInn')}{' '}
                          {submitting && <FontAwesomeIcon spin icon={faSpinner} />}
                        </Button>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              )}
            </Form>
          )
        }}
      </Formik>
    </div>
  )
}

export default CreateApplication
